import RetailerApi from '@/api/retailer-api';
import SizeChartApi from '@/api/size-chart';
import {FulfillmentOptionTypes} from '@/constants/fulfillment-options';
import {DeliveryModeTypes} from '@/constants/delivery-modes';
import {ConsumerProductPriceData} from '@/components/containers/pdp/utils/consumer-product-price-data';
import {ProductPriceData} from '@/components/containers/pdp/utils/product-price-data';
import store from '@/store';
import isEmpty from 'lodash/isEmpty';

const mutationTypes = Object.freeze({
  SET_RPA_VIEW_IN_STOCK: 'setRpaViewInStock',
  SET_PDP_DETAILS_LOADED: 'setPdpDetailsLoaded',
  SET_REQUESTED_PRODUCT_CODE: 'setRequestedProductCode',
  SET_SEARCH_RESULT_REFERENCE: 'setSearchResultReference',
  SET_SIZE_CHART_DATA: 'setSizeChartData',
  SET_MECHANIC_NOTES: 'setMechanicNotes',
  SET_LOCALLY_STOCK_AVAILABLE_RETAILERS: 'setLocallyStockAvailableRetailers',
  SET_LOCALLY_AVAILABLE_VARIANTS: 'setLocallyAvailableVariants',
  SET_IS_LOCALLY_CONSUMER_CHOICE_ACTIVE: 'setIsLocallyConsumerChoiceActive',
  SET_FULFILLMENT_OPTIONS: 'setFulfillmentOptions',
  SET_SELECTED_COLOR: 'setSelectedColor',
  SET_SELECTED_ATTRIBUTE: 'setSelectedAttribute',
  SET_PRODUCT_SPECS: 'setProductSpecs',
  SET_PRODUCT_PRICING: 'setProductPricing',
  SET_IS_BIKE_CUSTOMIZABLE: 'setIsBikeCustomizable',
  SET_IS_CUSTOMIZATION_MODE: 'setIsCustomizationMode',
  SET_TAB_HEADER_HEIGHT: 'setTabHeaderHeight',
  SET_IS_PDP: 'setIsPDP',
});

const ActionTypes = Object.freeze({
  FETCH_LOCALLY_AVAILABLE_VARIANTS: 'fetchLocallyAvailableVariants',
});

const productDetailsModule = {
  namespaced: true,
  state: {
    isPDP: false,
    isBike: false,
    rpaViewInStock: false,
    pdpDetailsLoaded: false,
    requestedProductCode: 0,
    searchResultReference: 0,
    electraAccessoryFinderToggle: false,
    pdpFunctionsMapping: {},
    fulfillmentOptions: [],
    activeTab: '',
    tabHeaderHeight: 0,
    primaryProductUrl: '',
    primaryProductName: '',
    primaryProductMetaData: [{key: '', value: ''}],
    productQuantity: null,
    sizeChartData: null,
    isBuyingZoneBundleDialogVisible: false,
    isGridBundleDialogVisible: false,
    variantCode: null,
    isECommAvailable: false,
    isLocallyActive: false,
    category: {},
    mechanicNotes: [],
    pdpBaseProductCode: 0,
    product: null,
    productSpecs: null,
    initAttributeSelection: {},
    locallyStockAvailableRetailers: [],
    selectedColor: '',
    selectedAttribute: '',
    images: [],
    shortSpecsToShow: [
      'shortSpecFrame',
      'shortSpecFrameFit',
      'shortSpecSuspension',
      'shortSpecFork',
      'shortSpecWheels',
      'shortSpecBrakeset',
      'shortSpecWeight',
      'shortSpecDrivetrain',
      'shortSpecTires',
      'shortSpecHandlebar',
      'shortSpecMotor',
      'shortSpecBattery',
      'shortSpecDisplay',
      'shortSpecTorque',
      'shortSpecRange',
      'shortSpecEbikeClassification',
    ],
    productPricing: {},
    isBikeCustomizable: false,
    isCustomizationMode: false,
    isUpdatingProductImages: true,
  },
  getters: {
    getActiveTab(state) {
      return state.activeTab;
    },
    locallyVariantProduct(state) {
      if (!state.product?.locallyAvailableVariants?.length) {
        return state.product;
      }
      const locallyAvailableVariantsSet = new Set(state.product.locallyAvailableVariants);
      const updatedVariants = state.product.variants.map((item) => ({
        ...item,
        isLocallyStockAvailable: locallyAvailableVariantsSet?.has(item.code),
      }));

      return {...state.product, variants: updatedVariants};
    },
    isBopisOnly(state) {
      return !!state.fulfillmentOptions.filter(
        (fo) =>
          fo?.code === FulfillmentOptionTypes.PICK_UP_IN_STORE &&
          fo?.deliveryModes?.every((mode) => mode.code.includes(DeliveryModeTypes.BOPIS))
      ).length;
    },
    getConsumerPriceForVariant: (state, getters) => (variantCode) => {
      let pricingData = state.productPricing[variantCode]?.consumerPrice || state.product?.consumerPrice;
      if (!pricingData && store.state.backend.isConsumerFriendlyMode) {
        return new ConsumerProductPriceData(pricingData, {
          b2b: store.state.backend.isB2Bsite,
        });
      }
      if (!pricingData || !Object.keys(pricingData).length) {
        return getters.getRetailerPriceForVariant(variantCode);
      }

      pricingData.apple = state.productPricing[variantCode]?.retailerPrice?.apple;
      pricingData.appleLabels = state.productPricing[variantCode]?.appleLabels;
      return new ConsumerProductPriceData(pricingData, {
        b2b: store.state.backend.isB2Bsite,
      });
    },
    getRetailerPriceForVariant: (state) => (variantCode) => {
      let pricingData = state.productPricing[variantCode]?.retailerPrice ?? {};
      if (!pricingData || !Object.keys(pricingData).length) {
        pricingData = state.product?.retailerPrice ?? {};
      }

      pricingData.advertised = state.productPricing[variantCode]?.consumerPrice?.advertised;
      pricingData.saleAdvertised = state.productPricing[variantCode]?.consumerPrice?.saleAdvertised;
      return new ProductPriceData(pricingData, {
        b2b: store.state.backend.isB2Bsite,
      });
    },
    hasPrice(state) {
      if (!state.productPricing || !state.productPricing[state.variantCode]?.retailerPrice) {
        return false;
      }
      const price =
        state.productPricing[state.variantCode]?.retailerPrice?.price?.formatted ??
        state.productPricing[state.variantCode]?.retailerPrice?.price?.low?.formatted;
      return !isEmpty(price);
    },
  },

  mutations: {
    [mutationTypes.SET_FULFILLMENT_OPTIONS](state, payload) {
      state.fulfillmentOptions = payload;
    },
    [mutationTypes.SET_SELECTED_COLOR](state, payload) {
      state.selectedColor = payload;
    },
    [mutationTypes.SET_SELECTED_ATTRIBUTE](state, payload) {
      state.selectedAttribute = payload;
    },
    [mutationTypes.SET_PRODUCT_SPECS](state, payload) {
      state.productSpecs = payload;
    },
    [mutationTypes.SET_PRODUCT_PRICING](state, payload) {
      state.productPricing[payload.code] = payload.prices;
    },
    [mutationTypes.SET_TAB_HEADER_HEIGHT](state, payload) {
      state.tabHeaderHeight = payload;
    },
    [mutationTypes.SET_IS_PDP](state, payload) {
      state.isPDP = payload;
    },

    setImages(state, images) {
      state.images = images;
    },
    setIsUpdatingProductImages(state, isUpdating) {
      state.isUpdatingProductImages = isUpdating;
    },

    setIsECommAvailable(state, payload) {
      state.isECommAvailable = payload;
    },

    setPriceData(state, payload) {
      state.priceData = payload;
    },

    setIsBike(state, payload) {
      state.isBike = payload;
    },

    setElectraAccessoryFinderToggle(state, payload) {
      state.electraAccessoryFinderToggle = payload;
    },

    setPdpFunctionsMapping(state, payload) {
      state.pdpFunctionsMapping = payload;
    },

    setRpaViewInStock(state, payload) {
      state.rpaViewInStock = payload;
    },

    setPdpDetailsLoaded(state, payload) {
      state.pdpDetailsLoaded = payload;
    },

    setRequestedProductCode(state, payload) {
      state.requestedProductCode = payload;
    },

    setSearchResultReference(state, payload) {
      state.searchResultReference = payload;
    },
    setActiveTab(state, payload) {
      state.activeTab = payload;
    },
    setPrimaryProductUrl(state, payload) {
      state.primaryProductUrl = payload;
    },

    setPrimaryProductName(state, payload) {
      state.primaryProductName = payload;
    },

    setPrimaryProductMetaData(state, payload) {
      state.primaryProductMetaData = payload;
    },

    setProductQuantity(state, payload) {
      state.productQuantity = payload;
    },
    setSizeChartData(state, payload) {
      state.sizeChartData = payload;
    },
    // Common mutation for two bundle modals. One for buying zone, other for sku-grid(both on pdp)
    setBundleDialogVisibility(state, payload) {
      if (payload.type === 'BUYING') {
        state.isBuyingZoneBundleDialogVisible = payload.isVisible;
      }

      if (payload.type === 'GRID') {
        state.isGridBundleDialogVisible = payload.isVisible;
      }
    },

    setVariantCode(state, payload) {
      state.variantCode = payload;
    },

    setIsLocallyActive(state, payload) {
      state.isLocallyActive = payload;
    },

    [mutationTypes.SET_IS_LOCALLY_CONSUMER_CHOICE_ACTIVE](state, payload) {
      state.isLocallyConsumerChoiceActive = payload;
    },

    setCategory(state, payload) {
      state.category = payload;
    },
    [mutationTypes.SET_MECHANIC_NOTES](state, payload) {
      state.mechanicNotes = payload ?? [];
    },
    setPdpBaseProductCode(state, payload) {
      state.pdpBaseProductCode = payload;
    },
    setProduct(state, payload) {
      state.product = payload;
    },
    setInitAttributeSelection(state, payload) {
      state.initAttributeSelection = payload;
    },
    [mutationTypes.SET_LOCALLY_STOCK_AVAILABLE_RETAILERS](state, payload) {
      state.locallyStockAvailableRetailers = payload;
    },
    [mutationTypes.SET_LOCALLY_AVAILABLE_VARIANTS](state, payload) {
      state.product = {...state.product, locallyAvailableVariants: payload};
    },
    [mutationTypes.SET_IS_BIKE_CUSTOMIZABLE](state, payload) {
      state.isBikeCustomizable = payload;
    },
    [mutationTypes.SET_IS_CUSTOMIZATION_MODE](state, payload) {
      state.isCustomizationMode = payload;
    },
  },

  actions: {
    setRpaViewInStockModalValue({commit}, viewInStockValue) {
      commit(mutationTypes.SET_RPA_VIEW_IN_STOCK, viewInStockValue);
    },
    updatePdpDetailsLoaded({commit}, pdpDetailsLoaded) {
      commit(mutationTypes.SET_PDP_DETAILS_LOADED, pdpDetailsLoaded);
    },
    updateRequestedProductCode({commit}, requestedProductCode) {
      commit(mutationTypes.SET_REQUESTED_PRODUCT_CODE, requestedProductCode);
    },
    updateSearchResultReference({commit}, searchResultReference) {
      commit(mutationTypes.SET_SEARCH_RESULT_REFERENCE, searchResultReference);
    },
    async fetchSizeChartData({commit}, productId) {
      try {
        const data = await SizeChartApi.getSizeChartData(productId);
        commit(mutationTypes.SET_SIZE_CHART_DATA, data.productSizeChart);
        return true;
      } catch (error) {
        console.error(error);
        return false;
      }
    },
    setInitAttributeSelection({commit}, selection) {
      commit('setInitAttributeSelection', selection);
    },
    setlocallyStockAvailableRetailers({commit}, retailers) {
      commit(mutationTypes.SET_LOCALLY_STOCK_AVAILABLE_RETAILERS, retailers);
    },
    async [ActionTypes.FETCH_LOCALLY_AVAILABLE_VARIANTS]({commit, rootState, state}) {
      try {
        const options = {
          productId: state.product.code,
          retailerId: rootState?.user?.selectedRetailer?.name,
        };
        const locallyAvailableVariantsResp = await RetailerApi.getExternalInventoryVariantsDetails(options);
        const locallyInStockVariantData = locallyAvailableVariantsResp.data?.locallyInStockVariants || [];
        commit(mutationTypes.SET_LOCALLY_AVAILABLE_VARIANTS, locallyInStockVariantData);
      } catch (error) {
        console.error('Error in getting locally Variants', error);
      }
    },
  },
};

export default productDetailsModule;
